import PublicLayout from '../../layouts/PublicLayout';
import { POLICY_VERSIONS, POLICY_LAST_UPDATED } from '../../lib/policies';

export default function PlatformDisclaimerPage() {
  return (
    <PublicLayout>
      <div className="container-main section-padding max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Platform Role & Liability Disclaimer
            </h1>
            <p className="text-slate-500">
              Last Updated: {POLICY_LAST_UPDATED} • Version {POLICY_VERSIONS.platform_disclaimer}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-blue-900 mb-2">Important Notice</h3>
            <p className="text-blue-800">
              Southwest Florida Vacation Rentals ("SWFL Rentals") operates exclusively as a 
              <strong> technology marketplace platform</strong>. We are not a property owner, 
              property manager, real estate broker, travel agent, or party to any rental agreement 
              between Hosts and Guests.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              1. What We Are
            </h2>
            <p className="text-slate-600 mb-4">
              SWFL Rentals is a technology platform that provides:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>A marketplace connecting property owners with travelers</li>
              <li>Listing and search functionality for vacation rentals</li>
              <li>Communication tools between Hosts and Guests</li>
              <li>Payment processing facilitation (through Stripe)</li>
              <li>Customer support and dispute assistance</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              2. What We Are NOT
            </h2>
            <div className="bg-slate-100 rounded-lg p-6">
              <p className="text-slate-700 mb-4">SWFL Rentals is expressly NOT:</p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li><strong>A property owner or landlord</strong> — We do not own any listed properties</li>
                <li><strong>A property manager</strong> — We do not manage, maintain, or control properties</li>
                <li><strong>A real estate broker or agent</strong> — We do not provide real estate services</li>
                <li><strong>A travel agency</strong> — We do not arrange travel services</li>
                <li><strong>A bank or escrow agent</strong> — Stripe handles all payment processing</li>
                <li><strong>An insurer</strong> — We do not provide insurance coverage</li>
                <li><strong>A party to rental agreements</strong> — Contracts are between Hosts and Guests</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              3. Contractual Relationships
            </h2>
            <p className="text-slate-600 mb-4">
              When a booking is confirmed:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>The rental agreement is directly between the Host and Guest</li>
              <li>SWFL Rentals is not a party to this agreement</li>
              <li>Hosts and Guests are bound by the listing terms and house rules</li>
              <li>SWFL Rentals' role is limited to facilitating the transaction</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              4. Limitation of Liability
            </h2>
            <p className="text-slate-600 mb-4">
              To the maximum extent permitted by law, SWFL Rentals is NOT liable for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>The condition, safety, quality, or legality of any listed property</li>
              <li>The accuracy of listing descriptions, photos, or claims</li>
              <li>The conduct or actions of Hosts or Guests</li>
              <li>Personal injury or property damage occurring at any property</li>
              <li>Theft, loss, or damage to personal belongings</li>
              <li>Host cancellations or booking modifications</li>
              <li>Disputes between Hosts and Guests</li>
              <li>Force majeure events affecting bookings</li>
              <li>Third-party services or websites linked from our platform</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              5. No Verification Guarantee
            </h2>
            <p className="text-slate-600 mb-4">
              While we may perform certain verification steps, we do NOT guarantee:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>The identity of any user</li>
              <li>The accuracy of user-provided information</li>
              <li>The legitimacy or licensing of any property</li>
              <li>Compliance with local laws or regulations</li>
              <li>The qualifications or background of any Host or Guest</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              6. Payment Processing Disclaimer
            </h2>
            <p className="text-slate-600 mb-4">
              All payments are processed by Stripe, a third-party payment processor:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>SWFL Rentals does not directly handle, hold, or transfer funds</li>
              <li>Stripe is responsible for payment security and processing</li>
              <li>Payment disputes are subject to Stripe's policies</li>
              <li>We are not a bank, escrow service, or money transmitter</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              7. User Responsibilities
            </h2>
            <p className="text-slate-600 mb-4">
              Users are solely responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Evaluating the suitability of properties and users</li>
              <li>Ensuring compliance with applicable laws</li>
              <li>Obtaining appropriate insurance coverage</li>
              <li>Verifying information before booking</li>
              <li>Exercising appropriate judgment and caution</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              8. Indemnification
            </h2>
            <p className="text-slate-600 mb-4">
              You agree to indemnify, defend, and hold harmless SWFL Rentals, its officers, 
              directors, employees, and agents from any claims, damages, losses, or expenses 
              arising from:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Your use of the platform</li>
              <li>Your breach of these terms or any policy</li>
              <li>Your violation of any law or third-party rights</li>
              <li>Any dispute between you and another user</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              9. Dispute Assistance
            </h2>
            <p className="text-slate-600 mb-4">
              While we are not liable for user disputes, we may:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide communication channels for dispute resolution</li>
              <li>Offer mediation assistance on a voluntary basis</li>
              <li>Review reported violations of platform policies</li>
              <li>Take enforcement action against policy violators</li>
            </ul>
            <p className="text-slate-600">
              Any assistance provided is at our sole discretion and does not create any obligation 
              or liability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              10. Acknowledgment
            </h2>
            <div className="bg-slate-100 rounded-lg p-6">
              <p className="text-slate-700">
                By using SWFL Rentals, you acknowledge that you have read, understood, and agree 
                to this Platform Role & Liability Disclaimer. You understand that SWFL Rentals is 
                a marketplace platform only and assumes no liability beyond its role as a technology 
                provider connecting Hosts and Guests.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              11. Contact
            </h2>
            <p className="text-slate-600">
              For questions about this disclaimer, please contact us at:<br />
              Email: legal@southwestflvacationrentals.com
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
